import React, { useState, useRef, useEffect, useCallback } from "react";
import { Currency } from "../types/enums";

const StakeInput: React.FC<StakeInputProps> = ({
  value,
  onStakeChange,
  currency = Currency.UGX,
  placeholder = "Stake",
  className = "w-16 py-1 bg-input-bg text-sm text-right focus:outline-none",
  onFocus,
  readOnly = false,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(
    value && value > 0 ? value.toString() : "",
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync internal state with prop changes
  useEffect(() => {
    // Show empty string for 0 values to make the input appear cleared
    setInputValue(value && value > 0 ? value.toString() : "");
  }, [value]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        onStakeChange(newValue);
      }, 300);
    },
    [onStakeChange],
  );

  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  return (
    <div className="flex items-center space-x-2 h-[31px] px-2 bg-input-bg border border-solid border-input-stake-border text-right">
      <input
        ref={inputRef}
        type="number"
        step="0.01"
        min="0"
        placeholder={placeholder}
        value={inputValue}
        className={className}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onClick={handleFocus}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        readOnly={readOnly}
        disabled={disabled}
        // Additional mobile-specific attributes
        inputMode="decimal"
        pattern="[0-9]*"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
      <span className="text-sm border-l border-solid pl-2 text-gray-500">
        {currency}
      </span>
    </div>
  );
};

export default StakeInput;
